/**
 * FomoNotification - Individual FOMO notification component
 *
 * Displays a single social proof notification with animation.
 */

import { motion } from 'framer-motion'
import { UserPlus, LogIn, Zap, Trophy, TrendingUp, Award, X } from 'lucide-react'

// Icon mapping for event types
const EVENT_ICONS = {
  signup: UserPlus,
  login: LogIn,
  challenge_started: Zap,
  challenge_completed: Trophy,
  level_up: TrendingUp,
  ranking_achieved: Award
}

// Color mapping for event types
const EVENT_COLORS = {
  signup: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    icon: 'text-green-600 dark:text-green-400'
  },
  login: {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    icon: 'text-cyan-600 dark:text-cyan-400'
  },
  challenge_started: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    icon: 'text-blue-600 dark:text-blue-400'
  },
  challenge_completed: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    icon: 'text-yellow-600 dark:text-yellow-400'
  },
  level_up: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    icon: 'text-purple-600 dark:text-purple-400'
  },
  ranking_achieved: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    icon: 'text-orange-600 dark:text-orange-400'
  }
}

/**
 * Generate message for event type
 * @param {Object} event - The notification event
 * @returns {string} Human-readable message
 */
function getEventMessage(event) {
  const { eventType, publicName, eventData } = event

  switch (eventType) {
    case 'signup':
      return `${publicName} just joined!`

    case 'login':
      return `${publicName} is practicing now`

    case 'challenge_started':
      return `${publicName} started a challenge`

    case 'challenge_completed':
      if (eventData?.score) {
        return `${publicName} scored ${eventData.score}% on a challenge!`
      }
      return `${publicName} completed a challenge!`

    case 'level_up':
      if (eventData?.newLevel) {
        return `${publicName} reached Level ${eventData.newLevel}!`
      }
      return `${publicName} leveled up!`

    case 'ranking_achieved':
      return `${publicName} earned a new ranking!`

    default:
      return `${publicName} is making progress!`
  }
}

/**
 * Format location string
 * @param {Object} event - The notification event
 * @returns {string|null} Formatted location or null
 */
function getLocation(event) {
  const { geoCity, geoRegion } = event

  if (geoCity && geoRegion) {
    return `${geoCity}, ${geoRegion}`
  }
  if (geoCity) {
    return geoCity
  }
  if (geoRegion) {
    return geoRegion
  }
  return null
}

/**
 * Get time ago string (always "Just now" for FOMO effect)
 * @returns {string}
 */
function getTimeAgo() {
  return 'Just now'
}

export default function FomoNotification({ event, onClose, position = 'top-right' }) {
  if (!event) return null

  const Icon = EVENT_ICONS[event.eventType] || Trophy
  const colors = EVENT_COLORS[event.eventType] || EVENT_COLORS.challenge_completed
  const message = getEventMessage(event)
  const location = getLocation(event)

  // Animation variants based on position
  const getAnimationVariants = () => {
    if (position.includes('left')) {
      return {
        initial: { opacity: 0, x: -100, scale: 0.9 },
        animate: { opacity: 1, x: 0, scale: 1 },
        exit: { opacity: 0, x: -100, scale: 0.9 }
      }
    }
    if (position.includes('right')) {
      return {
        initial: { opacity: 0, x: 100, scale: 0.9 },
        animate: { opacity: 1, x: 0, scale: 1 },
        exit: { opacity: 0, x: 100, scale: 0.9 }
      }
    }
    // Middle positions - slide down/up
    if (position.includes('top')) {
      return {
        initial: { opacity: 0, y: -50, scale: 0.9 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: -50, scale: 0.9 }
      }
    }
    return {
      initial: { opacity: 0, y: 50, scale: 0.9 },
      animate: { opacity: 1, y: 0, scale: 1 },
      exit: { opacity: 0, y: 50, scale: 0.9 }
    }
  }

  const variants = getAnimationVariants()

  return (
    <motion.div
      initial={variants.initial}
      animate={variants.animate}
      exit={variants.exit}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 max-w-sm pointer-events-auto"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${colors.bg}`}
        >
          <Icon className={`w-5 h-5 ${colors.icon}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{message}</p>

          {location && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{location}</p>
          )}

          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{getTimeAgo()}</p>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  )
}
